import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, Truck, FileText, ShoppingCart, Receipt, Package,
  FileCode, MessageSquare, Upload, Settings, Menu, Database, X,
  HardHat, BarChart3, UsersRound, Clock, TrendingDown, Brain, ChevronRight, Ruler
} from 'lucide-react';
import { IS_TAURI } from '@/core/environment';
import { APP_NAME } from '@/config/branding';

const Sidebar = ({ isOpen, toggleSidebar }: { isOpen: boolean; toggleSidebar: () => void }) => {
  const coreNav = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/contacts', icon: Users, label: 'Clientes' },
    { path: '/suppliers', icon: Truck, label: 'Proveedores' },
    { path: '/invoices', icon: FileText, label: 'Facturas' },
    { path: '/quotes', icon: ShoppingCart, label: 'Presupuestos' },
    { path: '/expenses', icon: Receipt, label: 'Gastos' },
    { path: '/items', icon: Package, label: 'Catálogo' },
    { path: '/templates', icon: FileCode, label: 'Plantillas' },
    { path: '/inbox', icon: MessageSquare, label: 'Comunicaciones' },
    { path: '/import', icon: Upload, label: 'Importar' },
    { path: '/settings', icon: Settings, label: 'Configuración' },
  ];

  const constructionNav = [
    { path: '/obras', icon: HardHat, label: 'Obras', badge: '' },
    { path: '/seguimiento', icon: BarChart3, label: 'Seguimiento', badge: '' },
    { path: '/equipo', icon: UsersRound, label: 'Equipo', badge: '' },
    { path: '/control-horario', icon: Clock, label: 'Control Horario', badge: '' },
    { path: '/costes-reales', icon: TrendingDown, label: 'Costes Reales', badge: '' },
    { path: '/presupuestos-ia', icon: Brain, label: 'Presupuestos IA', badge: 'AI' },
    { path: '/planos', icon: Ruler, label: 'Editor de Planos', badge: '3D' },
    { path: '/mediciones', icon: FileText, label: 'Importar BC3', badge: '' },
    { path: '/configuracion-obra', icon: Settings, label: 'Configuración Obra', badge: '' },
  ];

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative overflow-hidden ${
      isActive
        ? 'bg-gradient-to-r from-amber-500/15 to-amber-600/5 text-amber-400 shadow-sm shadow-amber-500/5'
        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200 hover:translate-x-0.5'
    }`;

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-gray-800 rounded-lg shadow-md text-white"
        onClick={toggleSidebar}
      >
        <Menu size={24} />
      </button>

      <aside
        className={`fixed top-0 left-0 z-40 h-screen bg-[#0d1117]/95 backdrop-blur-xl border-r border-white/8 transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 w-60 flex flex-col`}
      >
        {/* Header — Premium branding */}
        <div className="px-5 py-5 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <HardHat size={18} className="text-white" />
            </div>
            <div>
              <span className="text-base font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                {APP_NAME}
              </span>
              <div className="flex items-center gap-1">
                {IS_TAURI ? (
                  <>
                    <Database size={9} className="text-emerald-500" />
                    <span className="text-[9px] text-emerald-600 font-medium">SQLite local</span>
                  </>
                ) : (
                  <span className="text-[9px] text-gray-600 font-medium">Construction Platform</span>
                )}
              </div>
            </div>
          </div>
          <button
            className="md:hidden absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-300"
            onClick={toggleSidebar}
          >
            <X size={18} />
          </button>
        </div>

        {/* Core Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5 scrollbar-thin">
          <p className="px-3 pb-2 text-[10px] uppercase tracking-widest font-bold text-gray-600">
            ERP General
          </p>
          {coreNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={linkCls}
              onClick={() => window.innerWidth < 768 && toggleSidebar()}
            >
              <item.icon size={16} className="flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
              <span className="flex-1">{item.label}</span>
              <ChevronRight size={12} className="opacity-0 group-hover:opacity-40 -translate-x-1 group-hover:translate-x-0 transition-all" />
            </NavLink>
          ))}

          {/* Construction Section */}
          <div className="pt-5 pb-2 flex items-center gap-2 px-3">
            <div className="h-px flex-1 bg-gradient-to-r from-amber-500/30 to-transparent" />
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-500/70 whitespace-nowrap">
              Construcción
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-amber-500/30 to-transparent" />
          </div>
          {constructionNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={linkCls}
              onClick={() => window.innerWidth < 768 && toggleSidebar()}
            >
              <item.icon size={16} className="flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-400 border border-purple-500/20">
                  {item.badge}
                </span>
              )}
              <ChevronRight size={12} className="opacity-0 group-hover:opacity-40 -translate-x-1 group-hover:translate-x-0 transition-all" />
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/8 px-5 py-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-600">
              v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.1.0'}
              {IS_TAURI ? ' · Desktop' : ' · Web'}
            </p>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[9px] text-emerald-600 font-medium">Online</span>
            </div>
          </div>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Sidebar;
